import type { TFunction } from "i18next"

import {
  NotificationToastCard,
  type NotificationToastCardProps
} from "../../../components/notifications/NotificationToastCard"
import { formatEventWindow } from "../../../components/notifications/toastTime"
import { showNotification } from "../../../utils/notification/show-notification"
import {
  NOTIFICATION_TYPE,
  type Notification
} from "../types/notification.types"
import { notificationContent } from "./notificationContent"

export type ToastCard = Omit<NotificationToastCardProps, "onAction" | "onClose">

const recordLine = (t: TFunction, kind: "project" | "lead", name: string) =>
  `${t(`notifications.content.record.${kind}`, kind === "lead" ? "Lead" : "Project")}: ${name}`

/** An event reminder: the event, whose it is, when, and how soon. */
export function reminderToast(
  reminder: Notification,
  { t, dateFormat }: { t: TFunction; dateFormat: string }
): ToastCard {
  const content = notificationContent(reminder)
  const title =
    reminder.target?.name ||
    content.subject?.label ||
    reminder.description ||
    reminder.verb
  const customer = content.customer ?? content.record?.customer ?? null
  const record = content.record
  const showRecord =
    record?.name && record.name !== customer && record.name !== title

  return {
    category: "calendar",
    heading: t("notifications.toast.upcomingEvent", "Upcoming event"),
    title,
    context: [
      customer
        ? t("notifications.toast.customer", {
            name: customer,
            defaultValue: "Customer: {{name}}"
          })
        : null,
      showRecord ? recordLine(t, record.kind, record.name) : null
    ],
    when: formatEventWindow(reminder.data?.start_at, reminder.data?.end_at, {
      dateFormat,
      today: t("notifications.toast.today", "Today")
    }),
    startsAt: reminder.data?.start_at,
    actionLabel:
      content.resolved === false
        ? null
        : t("notifications.toast.viewEvent", "View event")
  }
}

/** A message: who wrote, where, and what they said. */
export function messageToast(
  message: Notification,
  { t, canOpen }: { t: TFunction; canOpen: boolean }
): ToastCard {
  const content = notificationContent(message)
  // Only the server's own block names the record's kind; the legacy fallback
  // guesses it from the target, which for a message is the conversation.
  const record = message.content?.record ?? null
  const sender = message.actor?.name || ""
  const projectName = message.data?.project_name || record?.name || ""
  const heading =
    Number(message.notification_name) === NOTIFICATION_TYPE.MESSAGE_MENTION
      ? t("messages.toast.mention", "Mentioned you")
      : t("messages.toast.newMessage", "New message")

  return {
    category: "messages",
    heading,
    title: sender || projectName || heading,
    context: [
      sender && projectName
        ? recordLine(t, record?.kind ?? "project", projectName)
        : null
    ],
    body: message.description || message.data?.preview || null,
    actionLabel:
      canOpen && content.resolved !== false
        ? t("notifications.toast.openMessage", "Open message")
        : null
  }
}

/**
 * Opens the card as an antd notice. The notice's own close control and
 * padding are switched off because the card draws both; it is keyed by the
 * notification so closing or acting on one card leaves the others alone.
 */
export function openNotificationToast(
  card: ToastCard,
  {
    key,
    duration,
    role,
    onAction
  }: {
    key: string
    duration: number
    role: "alert" | "status"
    onAction?: () => void
  }
) {
  const close = () => showNotification.destroy(key)

  showNotification.open({
    key,
    message: null,
    description: (
      <NotificationToastCard
        {...card}
        onAction={
          onAction
            ? () => {
                onAction()
                close()
              }
            : undefined
        }
        onClose={close}
      />
    ),
    duration,
    role,
    closeIcon: null,
    className: "ezb-notification-toast",
    style: { width: "min(420px, calc(100vw - 32px))" }
  })
}
